import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LOCATION } from '@personal-area/common';
import { ComposedReferralData } from '@personal-area/global/users/models';
import { LOCAL_STORAGE, LocalStorageService } from '@personal-area/storage';
import { REFERRAL_QUERY_PARAMS } from '../constants/referral-query-params';
import { siteReferralData } from '../constants/storage-keys';

@Injectable({
  providedIn: 'root',
})
export class ReferralComposingService {
  constructor(
    private _route: ActivatedRoute,
    private _localStorageService: LocalStorageService,
    @Inject(DOCUMENT) private _document: Document,
    @Inject(LOCATION) private _location: Location,
    @Inject(LOCAL_STORAGE) private _localStorage: Storage,
  ) {
  }

  public getComposedUrlReferralData(langA2: string): ComposedReferralData | undefined {
    if (this._hasActualUrlParams) {
      return this._getComposedActualReferrerFromUrlParams();
    } else if (this._hasLegacyUrlParams) {
      return this._getComposedLegacyReferrerFromUrlParams(langA2);
    } else return undefined;
  }

  public getComposedStoredReferralData(langA2: string): ComposedReferralData | undefined {
    if (this._hasNoStoredReferrer) {
      return undefined;
    }

    if (this._hasSiteReferrerParams) {
      return this._getComposedSiteParams();
    }

    if (this._hasActualStoredParams) {
      return this._getComposedActualStoredParams();
    }

    if (this._hasLegacyStoredParams) {
      return this._getComposedLegacyStoredParams(langA2);
    }
  }

  private get _composedReferrer(): string {
    const [referrerUrl, referrerQueryParams] = this._document.referrer.split('?');
    const referrerPath = referrerUrl
      .replace(this._location.protocol + '//' + this._location.host, '');
    const parsedReferrerQueryParams = new URLSearchParams(referrerQueryParams);
    const accountParam = parsedReferrerQueryParams.has('account')
      ? `?account=${parsedReferrerQueryParams.get('account')}`
      : '';
    const resultReferrer = referrerPath + accountParam;
    return resultReferrer || this._location.pathname;
  }

  private get _hasNoStoredReferrer(): boolean {
    return !this._hasActualStoredParams
      && !this._hasLegacyStoredParams
      && !this._hasSiteReferrerParams;
  }

  private get _hasLegacyUrlParams(): boolean {
    const q = this._route.snapshot.queryParams;
    const k = Object.keys(q);
    return k
      .some((key) => REFERRAL_QUERY_PARAMS.legacy.includes(key));
  }

  private get _hasLegacyStoredParams(): boolean {
    return REFERRAL_QUERY_PARAMS.legacy
      .some(referralKey => this._localStorageService.check(referralKey, this._localStorage));
  }

  private get _hasActualStoredParams(): boolean {
    return REFERRAL_QUERY_PARAMS.actual
      .some(referralKey => this._localStorageService.check(referralKey, this._localStorage));
  }

  private get _hasActualUrlParams() {
    return Object.keys(this._route.snapshot.queryParams)
      .some((key) => REFERRAL_QUERY_PARAMS.actual.includes(key));
  }

  private get _hasSiteReferrerParams() {
    return this._localStorageService.check(siteReferralData, this._localStorage);
  }

  /** Takes value of legacy referrals (ppu=123123;ppk=firstPair;ppu=555;ppk=lastPair),
   * returns last value of passed param  */
  private _lastStoredLegacyPair = (referralParam: 'ppk' | 'ppu') => {
    const pairs = this._localStorageService.getItem('ppu', this._localStorage)
      ?.split(';')
      ?.filter(pair => pair.includes(referralParam));
    if (!pairs || !pairs.length) {
      return null;
    }
    return pairs[pairs.length - 1].split('=')[1];
  };

  private _getComposedActualReferrerFromUrlParams = (): ComposedReferralData => {
    /** We need to parse fbs_reflink param which is url by itself, so we should compose params from actual url and params at fbs_reflink*/
    const { fbs_reflink } = this._route.snapshot.queryParams;
    const innerLinkParams = new URLSearchParams(fbs_reflink);

    const getParam = (param: string) => this._route.snapshot.queryParams[param] || innerLinkParams.get(param);

    const ibl = getParam('ibl');
    const ibp = getParam('ibp');
    const ibk = getParam('ibk');

    if (!ibk && !ibp) {
      return {
        isOldPartnership: false,
        ibl: +ibl,
        ibk: null,
        ibp: null,
      };
    } else {
      if (ibk) {
        return {
          isOldPartnership: false,
          ibk: ibk,
          ibl: +ibl,
          ibp: null,
        };
      } else {
        return {
          isOldPartnership: false,
          ibp: +ibp,
          ibl: +ibl,
          ibk: null,
        };
      }
    }
  };

  private _getComposedLegacyReferrerFromUrlParams = (lang: string) => {
    return {
      isOldPartnership: true,
      ibk: this._route.snapshot.queryParams['ppk'],
      ibl: null,
      ibp: +this._route.snapshot.queryParams['ppu'],
      page: this._composedReferrer,
      lang,
    };
  };

  private _getComposedLegacyStoredParams = (lang: string) => {
    const ppu = this._lastStoredLegacyPair('ppu');
    const ibp = ppu ? +ppu : null;
    return {
      isOldPartnership: true,
      ibk: this._lastStoredLegacyPair('ppk'),
      ibl: null,
      page: this._composedReferrer,
      ibp,
      lang,
    };

  };

  private _getComposedActualStoredParams = (): ComposedReferralData | undefined => {
    if (!this._localStorageService.check('ibk', this._localStorage) && !this._localStorageService.check('ibp', this._localStorage)) {
      return {
        isOldPartnership: false,
        ibl: this._localStorageService.check('ibl', this._localStorage) ? +this._localStorageService.getItem('ibl', this._localStorage)! : null,
        ibk: null,
        ibp: null,
      };
    }
    if (this._localStorageService.check('ibk', this._localStorage)) {
      return {
        isOldPartnership: false,
        ibl: this._localStorageService.check('ibl', this._localStorage) ? +this._localStorageService.getItem('ibl', this._localStorage)! : null,
        ibk: this._localStorageService.getItem('ibk', this._localStorage),
        ibp: null,
      };
    }
    if (this._localStorageService.check('ibp', this._localStorage)) {
      return {
        isOldPartnership: false,
        ibl: this._localStorageService.check('ibl', this._localStorage) ? +this._localStorageService.getItem('ibl', this._localStorage)! : null,
        ibp: this._localStorageService.check('ibp', this._localStorage) ? +this._localStorageService.getItem('ibp', this._localStorage)! : null,
        ibk: null,
      };
    }
  };

  private _getComposedSiteParams = (): ComposedReferralData | undefined => {
    const storedParams: Record<string, string | number | null> = this._localStorageService.getItem(siteReferralData, this._localStorage)!;

    if (storedParams && (storedParams['ibp'] || storedParams['ibl'] || storedParams['ibk'])) {
      if (!storedParams['ibk'] && !storedParams['ibp']) {
        return {
          isOldPartnership: false,
          ibl: storedParams['ibl'] ? +storedParams['ibl'] : null,
          ibk: null,
          ibp: null,
        };
      } else {
        if (storedParams['ibk']) {
          return {
            isOldPartnership: false,
            ibl: storedParams['ibl'] ? +storedParams['ibl'] : null,
            ibk: storedParams['ibk'] + '',
            ibp: null,
          };
        } else {
          if (storedParams['ibp']) {
            return {
              isOldPartnership: false,
              ibl: storedParams['ibl'] ? +storedParams['ibl'] : null,
              ibp: +storedParams['ibp'],
              ibk: null,
            };
          } else return undefined;
        }
      }
    } else return undefined;
  };
}
